import { useEffect, useRef, useState } from "react"
import { Button, Table } from "react-bootstrap"
import DeleteModal from "./DeleteModal"
import AddEndorsementModal from "./AddEndorsementsModal"
import { useDispatch, useSelector } from "react-redux"
import { deleteEndorsement, getEndorsements, actions, reorderEndorsementsList } from "../../../data/endorsementSlice" // Assuming you have reorderEndorsements action
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { css } from "@emotion/react"
import Loader from "../../../components/Loader"
import { camelCaseToWords } from "../../../constant/utils"
import { useDrag, useDrop, DndProvider } from "react-dnd"
import { HTML5Backend } from "react-dnd-html5-backend"
import { useNavigate } from "react-router-dom"
import { byPrefixAndName } from "@awesome.me/kit-de80aecb6a/icons"

const { reorderEndorsements } = actions

const componentStyle = css`
    h2 {
        color: #fff;
    }
`

const Endorsements = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [showModal, setShowModal] = useState(false)
    const [currentEndorsementId, setCurrentEndorsementId] = useState("")
    const [showDeleteModal, setShowDeleteModal] = useState(false)

    const { data: endorsements, fetchStatus } = useSelector((state) => state.endorsement)

    const { role } = useSelector((state) => state.authentication)

    useEffect(() => {
        if (role === "Standard") {
            navigate("/admin")
        }
    }, [role])

    useEffect(() => {
        getEndorsementsList()
    }, [])

    const getEndorsementsList = () => {
        return dispatch(getEndorsements())
    }

    const handleCloseModal = () => {
        setShowModal(false)
        setCurrentEndorsementId({})
    }

    const handleEditEndorsement = (endorsement) => {
        setCurrentEndorsementId(endorsement)
        setShowModal(true)
    }

    const handleDeleteEndorsement = (endorsement) => {
        setCurrentEndorsementId(endorsement)
        setShowDeleteModal(true)
    }

    const handleConfirmDelete = () => {
        dispatch(deleteEndorsement(currentEndorsementId))
        setShowDeleteModal(false)
    }

    const handleCancelDelete = () => {
        setShowDeleteModal(false)
    }

    const moveEndorsement = (dragIndex, hoverIndex) => {
        dispatch(reorderEndorsements({ dragIndex, hoverIndex }))
    }

    const onEndDnd = () => {
        const ids = endorsements?.map((endorsement) => endorsement.id)
        dispatch(reorderEndorsementsList(ids))
    }

    const EndorsementRow = ({ endorsement, index }) => {
        const [{ isDragging }, drag] = useDrag({
            type: "ENDORSEMENT",
            item: { index },
            collect: (monitor) => ({
                isDragging: monitor.isDragging()
            })
        })

        const [, drop] = useDrop({
            accept: "ENDORSEMENT",
            hover(item, monitor) {
                if (!dragRef.current) {
                    return
                }
                const dragIndex = item.index
                const hoverIndex = index
                if (dragIndex === hoverIndex) {
                    return
                }
                const hoverBoundingRect = dragRef.current?.getBoundingClientRect()
                const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
                const clientOffset = monitor.getClientOffset()
                const hoverClientY = clientOffset.y - hoverBoundingRect.top
                if (
                    (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) ||
                    (dragIndex > hoverIndex && hoverClientY > hoverMiddleY)
                ) {
                    return
                }
                moveEndorsement(dragIndex, hoverIndex)
                item.index = hoverIndex
            },
            drop(item, monitor) {
                onEndDnd()
            }
        })

        const dragRef = useRef(null)

        drag(drop(dragRef))

        return (
            <tr
                ref={dragRef}
                style={{ opacity: isDragging ? 0 : 1 }}>
                <td style={{ textAlign: "center" }}>
                    <FontAwesomeIcon icon={byPrefixAndName.fas["arrows-up-down-left-right"]} />
                </td>
                <td>{endorsement.name}</td>
                <td>{camelCaseToWords(endorsement.type)}</td>
                <td>{endorsement.number}</td>
                <td>{endorsement.edition}</td>
                <td style={{ textAlign: "center" }}>
                    <Button
                        variant="light"
                        className="me-2 btn-sm"
                        onClick={() => handleEditEndorsement(endorsement.id)}>
                        <FontAwesomeIcon icon={byPrefixAndName.fal["pen-to-square"]} />
                    </Button>
                    <Button
                        className="btn-sm"
                        variant="light"
                        onClick={() => handleDeleteEndorsement(endorsement.id)}>
                        <FontAwesomeIcon
                            icon={byPrefixAndName.fal["trash"]}
                            className="icon-red"
                        />
                    </Button>
                </td>
            </tr>
        )
    }

    return (
        <div
            css={componentStyle}
            className="page-container"
            style={{ marginTop: "80px" }}>
            <Loader show={fetchStatus === "loading"} />
            <div className="d-flex justify-content-between align-items-center my-2">
                <h2>Endorsements</h2>
                <Button
                    className="btn-sm"
                    variant="light"
                    onClick={() => {
                        setCurrentEndorsementId("")
                        setShowModal(true)
                    }}>
                    Add Endorsement
                </Button>
            </div>

            <AddEndorsementModal
                show={showModal}
                onHide={handleCloseModal}
                selectedRowId={currentEndorsementId}
            />

            <DeleteModal
                showDeleteModal={showDeleteModal}
                handleCancelDelete={handleCancelDelete}
                currentEndorsementId={currentEndorsementId}
                handleConfirmDelete={handleConfirmDelete}
            />
            <div className="bg-white p-3">
                <DndProvider backend={HTML5Backend}>
                    <Table
                        striped
                        bordered
                        hover
                        className="mt-1">
                        <thead>
                            <tr>
                                <th style={{ width: "40px" }}></th>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Number</th>
                                <th>Editions</th>
                                <th style={{ width: "120px", textAlign: "center" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {endorsements?.map((endorsement, index) => (
                                <EndorsementRow
                                    key={endorsement.id}
                                    endorsement={endorsement}
                                    index={index}
                                />
                            ))}
                        </tbody>
                    </Table>
                </DndProvider>
            </div>
        </div>
    )
}

export default Endorsements
